import firebase from "firebase/app";
import "firebase/firestore";

/**
 * User Activity Logger
 *
 * Logs user activities to a 'logs' subcollection under each user document
 *
 * Log Schema:
 * {
 *   timestamp: Firestore Timestamp,
 *   action: string (e.g. 'login', 'event_created', 'event_published'),
 *   metadata: object (optional additional data),
 *   userAgent: string,
 *   ip: string (if available)
 * }
 */

class UserLogger {
  constructor() {
    this.db = null;
  }

  /**
   * Get Firestore instance, initializing if needed
   */
  getDB() {
    if (!this.db) {
      this.db = firebase.firestore();
    }
    return this.db;
  }

  /**
   * Log a user activity
   * @param {string} userId - The user's UID
   * @param {string} action - The action being logged
   * @param {object} metadata - Optional additional data
   */
  async logActivity(userId, action, metadata = {}) {
    try {
      const logEntry = {
        timestamp: firebase.firestore.FieldValue.serverTimestamp(),
        action: action,
        metadata: metadata,
        userAgent: navigator.userAgent || null,
        sessionId: this.getSessionId()
      };

      await this.getDB()
        .collection("users")
        .doc(userId)
        .collection("logs")
        .add(logEntry);

      console.log(`User activity logged: ${action}`, logEntry);
    } catch (error) {
      console.error("Error logging user activity:", error);
      // Don't throw error - logging failures shouldn't break app functionality
    }
  }

  /**
   * Log user login
   * @param {string} userId
   * @param {string} email
   */
  async logLogin(userId, email) {
    await this.logActivity(userId, "login", {
      email: email,
      loginMethod: "firebase_auth"
    });
  }

  /**
   * Log user logout
   * @param {string} userId
   */
  async logLogout(userId) {
    await this.logActivity(userId, "logout");
  }

  /**
   * Log event creation
   * @param {string} userId
   * @param {string} eventId
   * @param {string} eventTitle
   * @param {string} eventType
   */
  async logEventCreated(userId, eventId, eventTitle, eventType) {
    await this.logActivity(userId, "event_created", {
      eventId: eventId,
      eventTitle: eventTitle,
      eventType: eventType
    });
  }

  /**
   * Log event publishing
   * @param {string} userId
   * @param {string} eventId
   * @param {string} eventTitle
   */
  async logEventPublished(userId, eventId, eventTitle) {
    await this.logActivity(userId, "event_published", {
      eventId: eventId,
      eventTitle: eventTitle
    });
  }

  /**
   * Log event deletion
   * @param {string} userId
   * @param {string} eventId
   * @param {string} eventTitle
   */
  async logEventDeleted(userId, eventId, eventTitle) {
    await this.logActivity(userId, "event_deleted", {
      eventId: eventId,
      eventTitle: eventTitle
    });
  }

  /**
   * Log profile creation
   * @param {string} userId
   * @param {string} profileId
   * @param {string} profileName
   * @param {string} profileType
   */
  async logProfileCreated(userId, profileId, profileName, profileType) {
    await this.logActivity(userId, "profile_created", {
      profileId: profileId,
      profileName: profileName,
      profileType: profileType
    });
  }

  /**
   * Log venue creation
   * @param {string} userId
   * @param {string} venueId
   * @param {string} venueName
   */
  async logVenueCreated(userId, venueId, venueName) {
    await this.logActivity(userId, "venue_created", {
      venueId: venueId,
      venueName: venueName
    });
  }

  /**
   * Log Stripe account connection
   * @param {string} userId
   * @param {string} stripeAccountId
   */
  async logStripeConnected(userId, stripeAccountId) {
    await this.logActivity(userId, "stripe_connected", {
      stripeAccountId: stripeAccountId
    });
  }

  /**
   * Log ticket purchase
   * @param {string} userId
   * @param {string} orderId
   * @param {string} eventId
   * @param {number} quantity
   * @param {number} amount
   */
  async logTicketPurchase(userId, orderId, eventId, quantity, amount) {
    await this.logActivity(userId, "ticket_purchase", {
      orderId: orderId,
      eventId: eventId,
      quantity: quantity,
      amount: amount
    });
  }

  /**
   * Get or create session ID (simple implementation)
   */
  getSessionId() {
    let sessionId = sessionStorage.getItem("gobo_session_id");
    if (!sessionId) {
      sessionId =
        Date.now() +
        "_" +
        Math.random()
          .toString(36)
          .substr(2, 9);
      sessionStorage.setItem("gobo_session_id", sessionId);
    }
    return sessionId;
  }

  /**
   * Get recent logs for a user (for admin/debugging purposes)
   * @param {string} userId
   * @param {number} limit
   */
  async getRecentLogs(userId, limit = 50) {
    try {
      const logsRef = this.getDB()
        .collection("users")
        .doc(userId)
        .collection("logs")
        .orderBy("timestamp", "desc")
        .limit(limit);

      const snapshot = await logsRef.get();
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error("Error fetching user logs:", error);
      return [];
    }
  }
}

// Export singleton instance
export const userLogger = new UserLogger();
export default userLogger;
